<template>
  <div class="list--container">
    <!-- Loading State -->
    <div v-if="loading" class="recommended-loading">
      <div class="loading-header">
        <div class="skeleton skeleton--header-icon"></div>

        <div class="loading-header--content">
          <div class="skeleton skeleton--header-title"></div>
          <div class="skeleton skeleton--header-description"></div>
        </div>
      </div>

      <div class="row mx-n2 recommended-listing--grid">
        <div
          v-for="index in 3"
          :key="`recommended-loading-${index}`"
          class="col-12 px-2 d-flex align-stretch"
        >
          <div class="loading-card">
            <div class="skeleton skeleton--status"></div>
            <div class="skeleton skeleton--title"></div>
            <div class="skeleton skeleton--location"></div>
            <div class="skeleton skeleton--information"></div>
            <div class="skeleton skeleton--period"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- Header -->
      <section class="recommended-section--header">
        <div class="recommended-header--content">
          <div class="recommended-header--icon">
            <v-icon>mdi-star-four-points-outline</v-icon>
          </div>

          <div class="recommended-header--copy">
            <h2>Listing Rekomendasi Aktif Anda</h2>
            <p>
              Pantau periode aktif dan kelola listing yang sedang mendapatkan prioritas penayangan.
            </p>
          </div>
        </div>

        <div class="recommended-toolbar">
          <div class="recommended-toolbar--main">
            <form class="recommended-search" @submit.prevent="startSearch">
              <v-icon size="20">mdi-magnify</v-icon>

              <input
                v-model="recommendedSearchQuery"
                type="text"
                aria-label="Cari listing rekomendasi"
                placeholder="Cari nomor listing atau judul"
              />

              <button
                v-if="recommendedSearchQuery"
                type="button"
                aria-label="Hapus pencarian"
                @click="clearSearch"
              >
                <v-icon size="18">mdi-close</v-icon>
              </button>
            </form>

            <div class="recommended-sort">
              <v-select
                v-model="selectedSortBy"
                :items="sortOptions"
                item-text="text"
                item-value="value"
                hide-details
                solo
                flat
                dense
              />
            </div>

            <button type="button" class="recommended-filter-button" @click="openFilterModal">
              <v-icon size="19">mdi-tune</v-icon>
              <span>Filter</span>
              <strong v-if="activeAdvancedFilterCount">{{ activeAdvancedFilterCount }}</strong>
            </button>

            <router-link class="btn recommended-toolbar--add" to="/">
              <span>Tambah Listing Rekomendasi</span>
              <v-icon>mdi-arrow-right</v-icon>
            </router-link>
          </div>
        </div>
      </section>

      <recommended-listing-filter-modal
        v-model="showFilterModal"
        :property-types="filterPropertyTypes"
        :period-options="periodOptions"
        :listing-type-options="listingTypeOptions"
        :provinces="provinces"
        :cities="cities"
        :areas="areas"
        :property-type="draftPropertyType"
        :period-start="draftPeriodStart"
        :listing-type="draftListingType"
        :province="draftProvince"
        :city="draftCity"
        :area="draftArea"
        @update:property-type="draftPropertyType = $event"
        @update:period-start="draftPeriodStart = $event"
        @update:listing-type="draftListingType = $event"
        @update:province="draftProvince = $event"
        @update:city="draftCity = $event"
        @update:area="draftArea = $event"
        @change-province="changeDraftProvince"
        @change-city="changeDraftCity"
        @close="closeFilterModal"
        @clear="clearAdvancedFilters"
        @apply="applyAdvancedFilters"
      />

      <!-- Listing Grid -->
      <template v-if="formattedRecommendedListings && formattedRecommendedListings.length > 0">
        <div class="row mx-n2 recommended-listing--grid">
          <div
            v-for="(listing, index) in formattedRecommendedListings"
            :key="listing?.listing?.uuid || `recommended-listing-${index}`"
            class="col-12 px-2 d-flex align-stretch"
          >
            <v-card class="recommended--list-card">
              <div class="recommended-card--inner">
                <div class="recommended-card--main">
                  <div class="recommended-card--title-wrap">
                    <div class="recommended-card--icon">
                      <v-icon>mdi-home-city-outline</v-icon>
                    </div>

                    <div class="recommended-card--heading">
                      <router-link
                        class="fa--title"
                        :to="getMyListingRoute(listing?.listing)"
                        :aria-label="`Buka listing ${displayValue(listing?.listing?.title)}`"
                      >
                        {{ displayValue(listing?.listing?.title) }}
                      </router-link>

                      <button
                        v-if="listing?.listing?.listing_number"
                        :id="`copyToClipboardListingNumber-${listing.listing.listing_number}`"
                        class="listing--number recommended-listing--number"
                        type="button"
                        :aria-label="`Salin nomor listing ${listing.listing.listing_number}`"
                        @click="copyToClipboard(listing.listing.listing_number)"
                      >
                        <span>
                          {{ `${$t('general.listingNumber')}: ${listing.listing.listing_number}` }}
                        </span>

                        <v-icon>mdi-content-copy</v-icon>

                        <popover
                          v-show="copiedListingNumber === listing.listing.listing_number"
                        ></popover>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="recommended-card--status hide--in-tablet hide--in-mobile">
                  <div
                    class="recommended-period--status"
                    :class="`recommended-period--status-${getPeriodStatus(listing)}`"
                  >
                    <v-icon>
                      {{
                        getPeriodStatus(listing) === 'active'
                          ? 'mdi-calendar-check-outline'
                          : 'mdi-calendar-clock-outline'
                      }}
                    </v-icon>

                    <div>
                      <span>Status periode</span>

                      <strong>{{ getPeriodStatusText(listing) }}</strong>

                      <small>{{ getPeriodStatusDescription(listing) }}</small>
                    </div>
                  </div>
                </div>

                <div class="property-information">
                  <div class="property-information--header">
                    <div>
                      <h4>Detail listing</h4>
                    </div>

                    <div class="property-information--icon">
                      <v-icon size="19"> mdi-text-box-outline </v-icon>
                    </div>
                  </div>

                  <div class="property-detail--grid">
                    <div class="property-detail--item">
                      <span class="property-detail--label">
                        {{ $t('addListing.listingType') }}
                      </span>

                      <strong class="property-detail--value">
                        {{
                          listing?.listing?.listing_type === 'S'
                            ? $t('general.sell')
                            : $t('general.rent')
                        }}
                      </strong>
                    </div>

                    <div class="property-detail--item">
                      <span class="property-detail--label">
                        {{ $t('general.propertyType') }}
                      </span>

                      <strong class="property-detail--value">
                        {{ displayValue(listing?.listing?.property_type) }}
                      </strong>
                    </div>

                    <div class="property-detail--item">
                      <span class="property-detail--label">
                        {{ $t('general.province') }}
                      </span>

                      <strong class="property-detail--value">
                        {{ displayValue(listing?.listing?.province) }}
                      </strong>
                    </div>

                    <div class="property-detail--item">
                      <span class="property-detail--label">
                        {{ $t('general.city') }}
                      </span>

                      <strong class="property-detail--value">
                        {{ displayValue(listing?.listing?.city) }}
                      </strong>
                    </div>

                    <div class="property-detail--item property-detail--item-wide">
                      <span class="property-detail--label">
                        {{ $t('general.area') }}
                      </span>

                      <strong class="property-detail--value">
                        {{ displayValue(listing?.listing?.area) }}
                      </strong>
                    </div>
                  </div>
                </div>

                <div class="slot--container">
                  <div class="slot-section--header">
                    <div>
                      <h4>Periode listing rekomendasi</h4>
                    </div>

                    <div class="slot-section--count">
                      {{ listing.boosts?.length || 0 }}
                      Periode
                    </div>
                  </div>

                  <div v-if="listing.boosts && listing.boosts.length > 0" class="slot-list">
                    <div
                      v-for="(entry, boostIndex) in listing.boosts"
                      :key="entry.uuid"
                      class="slot--card"
                      :class="{ 'slot--card-active': isActiveBoost(entry) }"
                    >
                      <div v-if="isActiveBoost(entry)" class="slot-active--badge">Aktif</div>

                      <div class="slot-card--top">
                        <div class="slot-card--icon">
                          <v-icon size="22"> mdi-calendar-range-outline </v-icon>
                        </div>

                        <div class="slot-card--heading">
                          <strong class="slot-card--label">
                            Periode
                            {{ String(boostIndex + 1).padStart(2, '0') }}
                          </strong>

                          <span class="slot-card--date-text">
                            {{ formatDate(entry.start_at) }}
                            -
                            {{ formatDate(entry.end_at) }}
                          </span>
                        </div>
                      </div>

                      <div class="slot-date--range">
                        <div class="slot-date--item">
                          <div class="slot-date--icon">
                            <v-icon size="18"> mdi-calendar-start-outline </v-icon>
                          </div>

                          <div class="slot-date--content">
                            <span>Mulai</span>

                            <strong>
                              {{ $date.format(entry.start_at, 'd MMM yyyy') }}
                            </strong>
                          </div>
                        </div>

                        <div class="slot-date--divider">
                          <span></span>

                          <v-icon size="20"> mdi-chevron-right </v-icon>

                          <span></span>
                        </div>

                        <div class="slot-date--item slot-date--item-end">
                          <div class="slot-date--icon">
                            <v-icon size="18"> mdi-calendar-end-outline </v-icon>
                          </div>

                          <div class="slot-date--content">
                            <span>Berakhir</span>

                            <strong>
                              {{ $date.format(entry.end_at, 'd MMM yyyy') }}
                            </strong>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        class="change-recommended--button"
                        @click="changeRecommended(listing?.listing, entry)"
                      >
                        <v-icon size="20"> mdi-swap-horizontal </v-icon>

                        <span>
                          {{ $t('listing.modal.changeRecommended.btn') }}
                        </span>
                      </button>
                    </div>
                  </div>

                  <div v-else class="slot-empty">
                    <div class="slot-empty--icon">
                      <v-icon size="26"> mdi-calendar-remove-outline </v-icon>
                    </div>

                    <div>
                      <strong>Belum ada periode</strong>

                      <span> Jadwal rekomendasi belum tersedia. </span>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="meta" class="recommended-pagination">
          <pagination
            :push-state="false"
            :meta="meta"
            :maxVisibleButtons="5"
            @changePage="changePage"
          />
        </div>
      </template>

      <!-- Empty State -->
      <div v-else class="empty-state--wrapper">
        <div class="empty--container">
          <div class="empty-state--illustration">
            <div class="empty-state--shape empty-state--shape-one"></div>

            <div class="empty-state--shape empty-state--shape-two"></div>

            <div class="empty-state--icon">
              <v-icon>
                {{ searchQuery ? 'mdi-magnify-close' : 'mdi-home-search-outline' }}
              </v-icon>
            </div>
          </div>

          <template v-if="!hasActiveFilter">
            <div class="empty-state--eyebrow">Belum Ada Listing Rekomendasi Aktif</div>

            <h3>Pasang listing rekomendasi pertama Anda</h3>

            <p>
              Tingkatkan visibilitas properti dan dapatkan lebih banyak perhatian dari calon pembeli
              melalui fitur listing rekomendasi.
            </p>

            <router-link to="/" class="btn btn-primary empty--buy">
              <v-icon size="21"> mdi-plus </v-icon>

              <span> Pasang Listing Rekomendasi </span>
            </router-link>
          </template>

          <template v-else>
            <div class="empty-state--eyebrow">Hasil Tidak Ditemukan</div>

            <h3>Tidak ada listing yang sesuai</h3>

            <p>Kami tidak menemukan listing rekomendasi berdasarkan filter yang anda pilih.</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import HelperMixin from '@/mixins/helpers';
import LodashMixin from '@/mixins/lodash';
import { mapGetters, mapState } from 'vuex';
import Pagination from '@/components/utils/fractal-pagination.vue';
import Popover from '@/components/utils/popover.vue';
import RecommendedListingFilterModal from '@/components/recommended-listing/recommended-listing-filter-modal.vue';

export default {
  name: 'featured-agent-list',

  components: {
    Pagination,
    Popover,
    RecommendedListingFilterModal,
  },

  mixins: [HelperMixin, LodashMixin],

  data: () => ({
    copiedListingNumber: null,
    showFilterModal: false,
    draftPropertyType: null,
    draftPeriodStart: null,
    draftListingType: null,
    draftProvince: null,
    draftCity: null,
    draftArea: null,
    provinces: [],
    cities: [],
    areas: [],
    listingTypeOptions: [
      { label: 'Dijual', value: 'S' },
      { label: 'Disewa', value: 'R' },
    ],
    sortOptions: [
      { text: 'Periode Terdekat', value: 'nearest' },
      { text: 'Sedang Aktif', value: 'active' },
      { text: 'Periode Terbanyak', value: 'period_count' },
    ],
  }),

  computed: {
    ...mapState({
      meta: (state) => state.recommendedListing.meta,

      loading: (state) => state.recommendedListing.loading,

      searchQuery: (state) => state.recommendedListing.searchQuery,

      sortBy: (state) => state.recommendedListing.sortBy,

      filterPropertyType: (state) => state.recommendedListing.filterPropertyType,

      filterPeriodStart: (state) => state.recommendedListing.filterPeriodStart,

      filterListingType: (state) => state.recommendedListing.filterListingType,

      filterProvince: (state) => state.recommendedListing.filterProvince,

      filterCity: (state) => state.recommendedListing.filterCity,

      filterArea: (state) => state.recommendedListing.filterArea,

      propertyTypes: (state) => state.masters.propertyTypes,
    }),

    ...mapGetters({
      formattedRecommendedListings: 'recommendedListing/formattedRecommendedListings',
    }),

    listingCount() {
      return this.formattedRecommendedListings ? this.formattedRecommendedListings.length : 0;
    },

    filterPropertyTypes() {
      return this.propertyTypes.filter((propertyType) => propertyType?.id);
    },

    periodOptions() {
      const weekStart = this.getCurrentWeekStart();

      return Array.from({ length: 8 }, (_, index) => {
        const start = new Date(weekStart);
        start.setDate(start.getDate() + index * 7);

        const end = new Date(start);
        end.setDate(end.getDate() + 6);

        return {
          label: `${this.$date.format(start, 'd MMM yyyy')} - ${this.$date.format(
            end,
            'd MMM yyyy',
          )}`,
          value: this.formatNativeDate(start),
        };
      });
    },

    hasActiveFilter() {
      return Boolean(
        this.searchQuery ||
          this.filterPropertyType ||
          this.filterPeriodStart ||
          this.filterListingType ||
          this.filterProvince ||
          this.filterCity ||
          this.filterArea,
      );
    },

    activeAdvancedFilterCount() {
      return [
        this.filterPropertyType,
        this.filterPeriodStart,
        this.filterListingType,
        this.filterProvince,
        this.filterCity,
        this.filterArea,
      ].filter(Boolean).length;
    },

    recommendedSearchQuery: {
      get() {
        return this.searchQuery;
      },
      set(value) {
        this.$store.commit('recommendedListing/SET_SEARCH_QUERY', value);
        this.debounceSearch();
      },
    },

    selectedSortBy: {
      get() {
        return this.sortBy;
      },
      set(value) {
        this.$store.commit('recommendedListing/SET_SORT_BY', value);
        this.startSearch();
      },
    },
  },

  mounted() {
    this.loadFilterOptions();
  },

  methods: {
    async openFilterModal() {
      this.syncDraftFilters();
      this.showFilterModal = true;

      if (this.draftProvince) {
        await this.getCities(this.draftProvince);
      }

      if (this.draftCity) {
        await this.getAreas(this.draftCity);
      }
    },

    closeFilterModal() {
      this.showFilterModal = false;
    },

    syncDraftFilters() {
      this.draftPropertyType = this.filterPropertyType;
      this.draftPeriodStart = this.filterPeriodStart;
      this.draftListingType = this.filterListingType;
      this.draftProvince = this.filterProvince;
      this.draftCity = this.filterCity;
      this.draftArea = this.filterArea;
    },

    async changeDraftProvince(province) {
      this.draftCity = null;
      this.draftArea = null;
      this.cities = [];
      this.areas = [];

      if (province?.id) {
        this.cities = await this.$store.dispatch('masters/getCitiesByProvinceId', province.id);
      }
    },

    async changeDraftCity(city) {
      this.draftArea = null;
      this.areas = [];

      if (city?.id) {
        this.areas = await this.$store.dispatch('masters/getAreasByCityId', city.id);
      }
    },

    applyAdvancedFilters() {
      this.$store.commit('recommendedListing/SET_FILTER_PROPERTY_TYPE', this.draftPropertyType);
      this.$store.commit('recommendedListing/SET_FILTER_PERIOD_START', this.draftPeriodStart);
      this.$store.commit('recommendedListing/SET_FILTER_LISTING_TYPE', this.draftListingType);
      this.$store.commit('recommendedListing/SET_FILTER_PROVINCE', this.draftProvince);
      this.$store.commit('recommendedListing/SET_FILTER_CITY', this.draftCity);
      this.$store.commit('recommendedListing/SET_FILTER_AREA', this.draftArea);
      this.closeFilterModal();
      this.startSearch();
    },

    async loadFilterOptions() {
      if (!this.propertyTypes.length) {
        await this.$store.dispatch('masters/getPropertyTypes');
      }

      this.provinces = await this.$store.dispatch('masters/getProvinces');

      if (this.filterProvince) {
        await this.getCities(this.filterProvince);
      }

      if (this.filterCity) {
        await this.getAreas(this.filterCity);
      }
    },

    async getCities(province) {
      this.cities = [];
      this.areas = [];

      if (province?.id) {
        this.cities = await this.$store.dispatch('masters/getCitiesByProvinceId', province.id);
      }
    },

    async getAreas(city) {
      this.areas = [];

      if (city?.id) {
        this.areas = await this.$store.dispatch('masters/getAreasByCityId', city.id);
      }
    },

    getCurrentWeekStart() {
      const date = new Date();
      const day = date.getDay() || 7;
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - day + 1);

      return date;
    },

    formatNativeDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    displayValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }

      return value;
    },
    isActiveBoost(entry) {
      return Boolean(entry && entry.is_active);
    },

    getBoosts(listing) {
      return listing?.boosts || [];
    },

    getActiveBoost(listing) {
      return this.getBoosts(listing).find((entry) => this.isActiveBoost(entry));
    },

    formatDate(value) {
      return value ? this.$date.format(value, 'd MMM yyyy') : '-';
    },

    getPeriodStatus(listing) {
      if (this.getActiveBoost(listing)) {
        return 'active';
      }

      return this.getBoosts(listing).length > 0 ? 'scheduled' : 'empty';
    },

    getPeriodStatusText(listing) {
      const status = this.getPeriodStatus(listing);

      if (status === 'active') {
        return 'Aktif';
      }

      return status === 'scheduled' ? 'Terjadwal' : 'Belum ada periode';
    },

    getPeriodStatusDescription(listing) {
      const activeBoost = this.getActiveBoost(listing);

      if (activeBoost) {
        return `${this.formatDate(activeBoost.start_at)} - ${this.formatDate(activeBoost.end_at)}`;
      }

      const periodCount = this.getBoosts(listing).length;

      return periodCount > 0 ? `${periodCount} periode tersedia` : 'Tambahkan periode rekomendasi';
    },

    getMyListingRoute(listing) {
      const listingNumber = listing?.listing_number || '';

      return listingNumber ? `/?q=${encodeURIComponent(listingNumber)}` : '/';
    },

    copyToClipboard(listingNumber) {
      navigator.clipboard.writeText(listingNumber);
      this.copiedListingNumber = listingNumber;

      setTimeout(() => {
        if (this.copiedListingNumber === listingNumber) {
          this.copiedListingNumber = null;
        }
      }, 2000);
    },

    startSearch() {
      this.$store.commit('recommendedListing/SET_PAGE', 1);
      this.$store.dispatch('recommendedListing/getRecommendedListings', {
        page: 1,
      });
    },

    debounceSearch: debounce(function () {
      this.startSearch();
    }, 500),

    clearSearch() {
      this.$store.commit('recommendedListing/SET_SEARCH_QUERY', null);
      this.startSearch();
    },

    clearAdvancedFilters() {
      this.cities = [];
      this.areas = [];
      this.draftPropertyType = null;
      this.draftPeriodStart = null;
      this.draftListingType = null;
      this.draftProvince = null;
      this.draftCity = null;
      this.draftArea = null;
      this.$store.commit('recommendedListing/SET_FILTER_PROPERTY_TYPE', null);
      this.$store.commit('recommendedListing/SET_FILTER_PERIOD_START', null);
      this.$store.commit('recommendedListing/SET_FILTER_LISTING_TYPE', null);
      this.$store.commit('recommendedListing/SET_FILTER_PROVINCE', null);
      this.$store.commit('recommendedListing/SET_FILTER_CITY', null);
      this.$store.commit('recommendedListing/SET_FILTER_AREA', null);
      this.startSearch();
    },

    clearFilters() {
      this.cities = [];
      this.areas = [];
      this.$store.commit('recommendedListing/SET_SEARCH_QUERY', null);
      this.clearAdvancedFilters();
    },

    getLocation(listing) {
      if (!listing) {
        return 'Lokasi belum tersedia';
      }

      const locations = [listing.area, listing.city, listing.province].filter(Boolean);

      return locations.length ? locations.join(', ') : 'Lokasi belum tersedia';
    },

    changePage(page) {
      this.$store.commit('recommendedListing/SET_PAGE', page);

      this.$store.dispatch('recommendedListing/getRecommendedListings', {
        page,
      });
    },

    changeRecommended(listing, entry) {
      this.$modal.show('change-recommended-modal', {
        submitAction: (selectedListing) => this.changeRecommendedAction(selectedListing, entry),

        listing,
        entry,
      });
    },

    async changeRecommendedAction(listing, entry) {
      try {
        const success = await this.$store.dispatch('recommendedListing/changeRecommended', {
          listingUuid: listing.uuid,
          boostUuid: entry.uuid,
        });

        if (success) {
          this.$emit('actionDone');
        }
      } catch (error) {
        console.log('ERROR CHANGE RECOMMENDED LISTING: ', error);
        throw error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/utils/variables';

$brand-red: #c81919;
$brand-red-dark: #8f1010;
$brand-red-soft: #ffffff;

$neutral-950: #18181b;
$neutral-900: #202024;
$neutral-800: #27272a;
$neutral-700: #3f3f46;
$neutral-600: #52525b;
$neutral-500: #71717a;
$neutral-400: #a1a1aa;
$neutral-300: #d4d4d8;
$neutral-200: #e4e4e7;
$neutral-150: #ececee;
$neutral-100: #f4f4f5;
$neutral-50: #fafafa;

.list--container {
  width: 100%;
  min-width: 0;
}

/* Header */
.recommended-section--header {
  position: relative;
  display: flex;
  min-height: 146px;
  align-items: stretch;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 25px 28px 25px 32px;
  overflow: hidden;
  border: 1px solid $neutral-150;
  border-radius: 24px;
  background: $color-white;
  box-shadow: 0 12px 34px rgba(24, 24, 27, 0.055);
  gap: 24px;
}

.recommended-header--decoration {
  position: absolute;
  top: 24px;
  bottom: 24px;
  left: 0;
  width: 5px;
  border-radius: 0 10px 10px 0;
  background: $brand-red;
}

.recommended-header--content {
  position: relative;
  z-index: 1;
  display: flex;
  width: 40%;
  flex: 0 1 40%;
  min-width: 0;
  align-items: center;
  gap: 17px;
}

.recommended-header--icon {
  display: inline-flex;
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  align-items: center;
  justify-content: center;
  border: 1px solid $brand-red;
  border-radius: 18px;
  background: $brand-red;

  .v-icon {
    color: $color-white;
    font-size: 29px;
  }
}

.recommended-header--copy {
  min-width: 0;

  h2 {
    margin: 3px 0 6px;
    color: $neutral-950;
    font-family: Poppins-Bold, sans-serif;
    font-size: 26px;
    line-height: 1.3;
  }

  p {
    max-width: 650px;
    margin: 0;
    color: $neutral-500;
    font-family: Poppins-Regular, sans-serif;
    font-size: 15px;
    line-height: 1.65;
  }
}

.recommended-header--eyebrow {
  color: $brand-red;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.recommended-header--summary {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 125px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 18px;
  border: 1px solid $neutral-150;
  border-radius: 16px;
  background: $neutral-50;
}

.summary--value {
  color: $neutral-950;
  font-family: Poppins-Bold, sans-serif;
  font-size: 28px;
  line-height: 1.2;
}

.summary--label {
  margin-top: 3px;
  color: $neutral-500;
  font-family: Poppins-Medium, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
}

.recommended-toolbar {
  display: flex;
  width: auto;
  min-width: 0;
  flex: 1 1 60%;
  flex-direction: column;
  align-items: stretch;
  align-self: center;
  padding: 12px;
  border: 1px solid $neutral-150;
  border-radius: 18px;
  background: $neutral-50;
  gap: 12px;
}

.recommended-toolbar--main {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.recommended-toolbar--add {
  display: inline-flex;
  min-height: 52px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 18px;
  border: 1px solid $brand-red;
  border-radius: 14px;
  color: $color-white;
  background: $brand-red;
  box-shadow: 0 8px 18px rgba(200, 25, 25, 0.16);
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 14px;
  line-height: 1.35;
  white-space: nowrap;
  transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;

  .v-icon {
    color: inherit;
    font-size: 15px;
  }

  &:hover {
    color: $color-white;
    background: darken($brand-red, 6%);
    box-shadow: 0 10px 22px rgba(200, 25, 25, 0.2);
    text-decoration: none;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.recommended-search {
  display: flex;
  min-width: 0;
  height: 52px;
  flex: 1 1 auto;
  align-items: center;
  padding: 0 14px;
  border: 1px solid $neutral-150;
  border-radius: 14px;
  background: $color-white;
  gap: 10px;

  .v-icon {
    flex: 0 0 auto;
    color: $neutral-500;
  }

  input {
    min-width: 0;
    flex: 1;
    border: 0;
    outline: 0;
    color: $neutral-800;
    background: transparent;
    font-family: Poppins-Regular, sans-serif;
    font-size: 14px;
    line-height: 1.4;

    &::placeholder {
      color: $neutral-400;
    }
  }

  button {
    display: inline-flex;
    width: 30px;
    height: 30px;
    flex: 0 0 30px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: $color-white;
  }
}

.recommended-sort {
  width: 190px;
  flex: 0 0 190px;

  ::v-deep .v-input__slot {
    min-height: 52px !important;
    border: 1px solid $neutral-150;
    border-radius: 14px !important;
    background: $color-white !important;
    box-shadow: none !important;
  }

  ::v-deep .v-select__selection {
    color: $neutral-800;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 14px;
  }
}

.recommended-filter-button {
  position: relative;
  display: inline-flex;
  height: 52px;
  min-width: 122px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border: 1px solid $brand-red;
  border-radius: 14px;
  color: $brand-red;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 14px;
  gap: 8px;
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;

  .v-icon {
    color: $brand-red;
  }

  strong {
    display: inline-flex;
    min-width: 22px;
    height: 22px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: $color-white;
    background: $brand-red;
    font-family: Poppins-Bold, sans-serif;
    font-size: 12px;
    line-height: 1;
  }

  &:hover {
    border-color: $brand-red;
    background: $color-white;
    transform: translateY(-1px);
  }
}

/* Listing Grid */
.recommended-listing--grid {
  row-gap: 16px;
}

.recommended--list-card {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid $neutral-150 !important;
  border-radius: 20px !important;
  background: $color-white !important;
  box-shadow: 0 8px 26px rgba(24, 24, 27, 0.055) !important;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;

  &:hover {
    border-color: rgba(200, 25, 25, 0.22) !important;
    box-shadow: 0 16px 42px rgba(24, 24, 27, 0.085) !important;
    transform: translateY(-2px);

    .recommended-card--icon {
      border-color: $brand-red;
      background: $brand-red;

      .v-icon {
        color: $color-white;
      }
    }
  }
}

.recommended-card--accent {
  position: absolute;
  top: 0;
  right: 24px;
  left: 24px;
  height: 4px;
  border-radius: 0 0 10px 10px;
  background: $brand-red;
}

.recommended-card--inner {
  display: grid;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  align-items: stretch;
  padding: 18px;
  grid-template-areas:
    'main status slots'
    'details status slots';
  grid-template-columns: minmax(0, 1.15fr) 250px minmax(360px, 1fr);
  gap: 16px;
}

.recommended-card--main {
  min-width: 0;
  grid-area: main;
}

/* Status */
.recommended-card--status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  gap: 12px;
}

.recommended-status--badge {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  padding: 7px 11px;
  border: 1px solid rgba(22, 163, 74, 0.16);
  border-radius: 100px;
  color: #15803d;
  background: #f0fdf4;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

.recommended-status--indicator {
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  margin-right: 8px;
  border-radius: 50%;
  background: #16a34a;
  box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.12);
}

/* Listing Heading */
.recommended-card--title-wrap {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 12px;
}

.recommended-card--icon {
  display: inline-flex;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(200, 25, 25, 0.2);
  border-radius: 14px;
  background: $color-white;
  transition: background-color 0.25s ease, border-color 0.25s ease;

  .v-icon {
    color: $brand-red;
    font-size: 24px;
    transition: color 0.25s ease;
  }
}

.recommended-card--heading {
  min-width: 0;
  flex: 1;
}

.fa--title {
  display: -webkit-box;
  min-height: 0;
  margin: 0;
  overflow: hidden;
  color: $neutral-950;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 18px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  text-decoration: none;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  &:hover {
    color: $brand-red;
    text-decoration: none;
  }

  &:focus-visible {
    border-radius: 6px;
    outline: 3px solid rgba(200, 25, 25, 0.18);
    outline-offset: 3px;
  }
}

.recommended-listing--number {
  position: relative;
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  margin-top: 8px;
  padding: 6px 9px;
  border: 1px solid $neutral-150;
  border-radius: 999px;
  color: $neutral-600;
  cursor: pointer;
  background: $neutral-50;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  gap: 6px;

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .v-icon {
    color: $neutral-500;
    font-size: 16px;
  }

  &:hover,
  &:hover .v-icon {
    color: $brand-red;
  }

  &:focus-visible {
    outline: 3px solid rgba(200, 25, 25, 0.16);
    outline-offset: 2px;
  }
}

.recommended-card--status {
  display: flex;
  min-width: 0;
  grid-area: status;
}

.recommended-period--status {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: flex-start;
  padding: 15px;
  border: 1px solid $neutral-150;
  border-radius: 16px;
  background: $color-white;
  gap: 12px;

  .v-icon {
    display: inline-flex;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    align-items: center;
    justify-content: center;
    border-radius: 11px;
    color: $brand-red;
    background: $color-white;
  }

  span,
  strong,
  small {
    display: block;
  }

  span {
    color: $neutral-500;
    font-family: Poppins-Regular, sans-serif;
    font-size: 12px;
    line-height: 1.4;
  }

  strong {
    margin-top: 3px;
    color: $neutral-950;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 16px;
    line-height: 1.35;
  }

  small {
    margin-top: 5px;
    color: $neutral-600;
    font-family: Poppins-Regular, sans-serif;
    font-size: 12px;
    line-height: 1.45;
  }
}

.recommended-period--status-active {
  border-color: rgba(22, 163, 74, 0.24);
  background: linear-gradient(180deg, #f0fdf4 0%, $color-white 100%);

  .v-icon {
    color: #15803d;
  }
}

.recommended-card--location {
  display: flex;
  min-height: 42px;
  min-width: 0;
  align-items: flex-start;
  margin-top: 5px;
  overflow: hidden;
  color: $neutral-500;
  font-family: Poppins-Regular, sans-serif;
  font-size: 13px;
  line-height: 1.55;
  gap: 5px;

  .v-icon {
    flex: 0 0 auto;
    margin-top: 1px;
    color: $neutral-500;
  }

  span {
    display: -webkit-box;
    min-width: 0;
    overflow: hidden;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}

/* Compact Property Information */
.property-information {
  min-width: 0;
  margin-top: 0;
  padding: 12px;
  border: 1px solid $neutral-150;
  border-radius: 14px;
  background: $neutral-50;
  grid-area: details;
}

.property-information--header,
.slot-section--header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 11px;

  h4 {
    margin: 0;
    color: $neutral-950;
    font-family: Poppins-SemiBold, sans-serif;
    line-height: 1.4;
  }
}

.property-information--header {
  margin-bottom: 10px;

  h4 {
    font-size: 14px;
  }
}

.property-information--eyebrow,
.slot-section--eyebrow {
  color: $neutral-500;
  font-family: Poppins-Medium, sans-serif;
  font-size: 10px;
  line-height: 1.4;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.property-information--icon {
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  border: 1px solid $neutral-150;
  border-radius: 8px;
  background: $color-white;

  .v-icon {
    color: $neutral-600;
  }
}

.property-detail--grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid $neutral-150;
  border-radius: 9px;
  background: $color-white;
}

.property-detail--item {
  display: block;
  min-width: 0;
  grid-column: span 2;
  padding: 10px 12px;
  border-bottom: 1px solid $neutral-150;
  border-right: 1px solid $neutral-150;

  &:nth-child(-n + 2) {
    grid-column: span 3;
  }

  &:nth-child(2),
  &:last-child {
    border-right: 0;
  }

  &:nth-last-child(-n + 3) {
    border-bottom: 0;
  }
}

.property-detail--item-wide {
  grid-column: span 2;
}

.property-detail--label {
  display: block;
  margin-bottom: 3px;
  color: $neutral-500;
  font-family: Poppins-Regular, sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

.property-detail--value {
  display: block;
  min-height: 0;
  max-height: none;
  min-width: 0;
  overflow: hidden;
  color: $neutral-800;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Larger Period Area */
.slot--container {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: none;
  flex-direction: column;
  margin-top: 0;
  padding: 14px;
  border: 1px solid $neutral-150;
  border-radius: 16px;
  background: $neutral-50;
  grid-area: slots;
}

.slot-section--header {
  flex: 0 0 auto;
  margin-bottom: 10px;

  h4 {
    margin-top: 2px;
    font-size: 16px;
  }
}

.slot-section--count {
  flex: 0 0 auto;
  padding: 6px 10px;
  border: 1px solid $neutral-150;
  border-radius: 9px;
  color: $neutral-600;
  background: $color-white;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

.slot-list {
  display: flex;
  min-width: 0;
  min-height: 0;
  max-height: 185px;
  flex: 1 1 auto;
  flex-direction: column;
  padding-right: 6px;
  overflow-x: hidden;
  overflow-y: auto;
  gap: 9px;
  overscroll-behavior: contain;
  scroll-snap-type: y mandatory;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: $neutral-300 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 999px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: $neutral-300;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: $neutral-400;
  }
}

.slot--card {
  position: relative;
  flex: 0 0 auto;
  min-height: 0;
  padding: 14px;
  overflow: hidden;
  border: 1px solid $neutral-150;
  border-radius: 14px;
  background: $color-white;
  scroll-snap-align: start;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: rgba(200, 25, 25, 0.2);
    box-shadow: 0 8px 20px rgba(24, 24, 27, 0.055);
  }
}

.slot--card-active {
  border-color: rgba(22, 163, 74, 0.38);
  background: linear-gradient(180deg, #f0fdf4 0%, $color-white 42%);

  .slot-card--icon {
    border-color: rgba(22, 163, 74, 0.18);
    background: #dcfce7;

    .v-icon {
      color: #15803d;
    }
  }
}

.slot-active--badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  padding: 4px 8px;
  border: 1px solid rgba(22, 163, 74, 0.18);
  border-radius: 999px;
  color: #15803d;
  background: #f0fdf4;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 11px;
  line-height: 1.4;
}

.slot-card--top {
  position: relative;
  z-index: 1;
  display: flex;
  padding-right: 84px;
  align-items: center;
  margin-bottom: 11px;
  gap: 10px;
}

.slot-card--icon {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(200, 25, 25, 0.2);
  border-radius: 9px;
  background: $color-white;

  .v-icon {
    color: $brand-red;
  }
}

.slot-card--heading {
  min-width: 0;
}

.slot-card--eyebrow {
  display: block;
  margin-bottom: 2px;
  color: $neutral-500;
  font-family: Poppins-Medium, sans-serif;
  font-size: 10px;
  line-height: 1.4;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.slot-card--label {
  display: block;
  color: $neutral-800;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 14px;
  line-height: 1.45;
}

.slot-card--date-text {
  display: block;
  margin-top: 2px;
  color: $neutral-500;
  font-family: Poppins-Regular, sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

.slot-date--range {
  display: grid;
  align-items: stretch;
  grid-template-columns:
    minmax(0, 1fr)
    26px
    minmax(0, 1fr);
  margin-bottom: 10px;
  gap: 6px;
}

.slot-date--item {
  display: flex;
  min-width: 0;
  align-items: center;
  padding: 9px;
  border: 1px solid $neutral-150;
  border-radius: 11px;
  background: $neutral-50;
  gap: 8px;
}

.slot-date--icon {
  display: inline-flex;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: $color-white;

  .v-icon {
    color: $neutral-500;
  }
}

.slot-date--content {
  min-width: 0;

  span,
  strong {
    display: block;
  }

  span {
    margin-bottom: 2px;
    color: $neutral-500;
    font-family: Poppins-Regular, sans-serif;
    font-size: 10px;
    line-height: 1.4;
  }

  strong {
    color: $neutral-800;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
  }
}

.slot-date--divider {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  span {
    width: 1px;
    height: 7px;
    background: $neutral-200;
  }

  .v-icon {
    color: $neutral-400;
  }
}

.change-recommended--button {
  display: flex;
  width: 100%;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;
  border: 1px solid $brand-red;
  border-radius: 11px;
  outline: none;
  color: $color-white;
  background: $brand-red;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  cursor: pointer;
  gap: 8px;
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease;

  .v-icon {
    color: $color-white;
    transition: color 0.2s ease;
  }

  &:hover {
    border-color: $brand-red-dark;
    color: $color-white;
    background: $brand-red-dark;
    transform: translateY(-1px);

    .v-icon {
      color: $color-white;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(200, 25, 25, 0.14);
  }
}

.slot-empty {
  display: flex;
  min-height: 165px;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  padding: 22px;
  border: 1px dashed $neutral-300;
  border-radius: 14px;
  color: $neutral-500;
  background: $neutral-50;
  font-family: Poppins-Regular, sans-serif;
  gap: 12px;

  strong,
  span {
    display: block;
  }

  strong {
    margin-bottom: 3px;
    color: $neutral-700;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 14px;
  }

  span {
    font-size: 12px;
    line-height: 1.5;
  }
}

.slot-empty--icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  background: $color-white;

  .v-icon {
    color: $neutral-400;
  }
}

/* Pagination */
.recommended-pagination {
  margin-top: 28px;
}

/* Empty State */
.empty-state--wrapper {
  display: flex;
  min-height: 480px;
  align-items: center;
  justify-content: center;
  padding: 34px 16px;
}

.empty--container {
  width: 100%;
  max-width: 580px;
  padding: 38px 32px;
  border: 1px solid $neutral-150;
  border-radius: 26px;
  background: $color-white;
  box-shadow: 0 14px 40px rgba(24, 24, 27, 0.065);
  text-align: center;

  h3 {
    margin: 8px 0 10px;
    color: $neutral-950;
    font-family: Poppins-Bold, sans-serif;
    font-size: 23px;
    line-height: 1.4;
  }

  p {
    max-width: 470px;
    margin: 0 auto;
    color: $neutral-500;
    font-family: Poppins-Regular, sans-serif;
    font-size: 15px;
    line-height: 1.7;
  }
}

.empty-state--illustration {
  position: relative;
  display: flex;
  width: 108px;
  height: 108px;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
}

.empty-state--shape {
  position: absolute;
  border-radius: 50%;
}

.empty-state--shape-one {
  width: 108px;
  height: 108px;
  border: 1px solid rgba(200, 25, 25, 0.08);
  background: $brand-red-soft;
}

.empty-state--shape-two {
  right: 4px;
  bottom: 7px;
  width: 35px;
  height: 35px;
  border: 8px solid $color-white;
  background: rgba(200, 25, 25, 0.14);
}

.empty-state--icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 62px;
  height: 62px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(200, 25, 25, 0.12);
  border-radius: 20px;
  background: $color-white;
  box-shadow: 0 10px 24px rgba(200, 25, 25, 0.1);

  .v-icon {
    color: $brand-red;
    font-size: 31px;
  }
}

.empty-state--eyebrow {
  color: $brand-red;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.empty--buy {
  display: inline-flex;
  width: auto;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  padding: 12px 20px;
  border-radius: 12px;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  gap: 8px;
  box-shadow: 0 9px 20px rgba(200, 25, 25, 0.18);

  .v-icon {
    color: $color-white;
  }
}

/* Loading */
.recommended-loading {
  width: 100%;
}

.loading-header {
  display: flex;
  min-height: 146px;
  align-items: center;
  margin-bottom: 24px;
  padding: 25px 32px;
  border: 1px solid $neutral-150;
  border-radius: 24px;
  gap: 17px;
}

.loading-header--content {
  width: 100%;
  max-width: 540px;
}

.loading-card {
  width: 100%;
  min-height: 388px;
  padding: 16px;
  border: 1px solid $neutral-150;
  border-radius: 18px;
  background: $color-white;
}

.skeleton {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background: $neutral-100;

  &::after {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent);
    content: '';
    animation: skeleton-loading 1.35s infinite;
  }
}

.skeleton--header-icon {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border-radius: 18px;
}

.skeleton--header-title {
  width: 60%;
  height: 26px;
}

.skeleton--header-description {
  width: 90%;
  height: 15px;
  margin-top: 12px;
}

.skeleton--status {
  width: 145px;
  height: 32px;
}

.skeleton--title {
  width: 86%;
  height: 55px;
  margin-top: 22px;
}

.skeleton--location {
  width: 70%;
  height: 17px;
  margin-top: 10px;
}

.skeleton--information {
  width: 100%;
  height: 120px;
  margin-top: 16px;
  border-radius: 12px;
}

.skeleton--period {
  width: 100%;
  height: 150px;
  margin-top: 16px;
  border-radius: 12px;
}

@keyframes skeleton-loading {
  100% {
    left: 100%;
  }
}

@media (max-width: 1199px) {
  .recommended-section--header {
    flex-direction: column;
  }

  .recommended-header--content {
    width: 100%;
    max-width: none;
    flex: 0 0 auto;
  }

  .recommended-toolbar {
    width: 100%;
    min-width: 0;
    flex-basis: auto;
    align-self: stretch;
  }

  .recommended-card--inner {
    grid-template-areas:
      'main status'
      'details details'
      'slots slots';
    grid-template-columns: minmax(0, 1fr) 260px;
  }
}

/* Tablet */
@media (max-width: 959px) {
  .recommended-section--header {
    min-height: 138px;
    flex-direction: column;
    padding: 23px 24px 23px 28px;
  }

  .recommended-toolbar {
    width: 100%;
    min-width: 0;
    flex-basis: auto;
    align-self: stretch;
  }

  .recommended-header--copy {
    h2 {
      font-size: 23px;
    }

    p {
      font-size: 14px;
    }
  }

  .recommended-header--summary {
    min-width: 110px;
  }

  .recommended--list-card,
  .loading-card {
    min-height: 0;
  }

  .recommended-card--inner {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .slot--container {
    margin-top: 0;
  }

  .recommended--list-card {
    &:hover {
      transform: none;
    }
  }

  .slot-date--range {
    grid-template-columns:
      minmax(0, 1fr)
      20px
      minmax(0, 1fr);
  }

  .slot-date--item {
    padding: 10px 7px;
    gap: 6px;
  }

  .slot-date--icon {
    display: none;
  }

  .slot-date--content {
    strong {
      font-size: 11px;
    }
  }
}

/* Mobile */
@media (max-width: 599px) {
  .recommended-section--header {
    min-height: auto;
    flex-direction: column;
    align-items: stretch;
    padding: 21px 19px 19px 23px;
    border-radius: 20px;
    gap: 18px;

    &::after {
      right: -64px;
      bottom: -86px;
    }
  }

  .recommended-header--decoration {
    top: 20px;
    bottom: 20px;
    width: 4px;
  }

  .recommended-header--content {
    align-items: flex-start;
    gap: 13px;
  }

  .recommended-header--icon {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
    border-radius: 15px;

    .v-icon {
      font-size: 25px;
    }
  }

  .recommended-header--copy {
    h2 {
      margin-top: 3px;
      font-size: 20px;
      line-height: 1.4;
    }

    p {
      font-size: 14px;
      line-height: 1.65;
    }
  }

  .recommended-header--eyebrow {
    font-size: 11px;
  }

  .recommended-header--summary {
    min-width: 0;
    flex-direction: row;
    align-self: flex-start;
    padding: 9px 13px;
    gap: 7px;
  }

  .recommended-toolbar {
    flex-direction: column;
    align-items: stretch;
    padding: 10px;
  }

  .recommended-toolbar--add {
    width: 100%;
  }

  .recommended-toolbar--main {
    flex-direction: column;
    align-items: stretch;
  }

  .recommended-filter-button {
    width: 100%;
  }

  .recommended-sort {
    width: 100%;
    flex: 0 0 auto;
  }

  .summary--value {
    font-size: 20px;
  }

  .summary--label {
    margin-top: 0;
    font-size: 12px;
  }

  .recommended-listing--grid {
    row-gap: 14px;
  }

  .recommended--list-card,
  .loading-card {
    height: auto;
    min-height: 0;
    border-radius: 16px !important;
  }

  .recommended-card--inner {
    height: auto;
    min-height: 0;
    padding: 14px;
  }

  .recommended-card--accent {
    right: 20px;
    left: 20px;
  }

  .recommended-status--badge {
    font-size: 12px;
  }

  .recommended-card--icon {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
    border-radius: 13px;
  }

  .fa--title {
    height: auto;
    min-height: 0;
    font-size: 16px;
  }

  .recommended-card--location {
    min-height: 40px;
    font-size: 13px;
  }

  .property-information {
    padding: 11px;
  }

  .property-detail--grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .property-detail--item-wide {
    grid-column: auto;
  }

  .property-information--header {
    h4 {
      font-size: 14px;
    }
  }

  .property-information--eyebrow,
  .slot-section--eyebrow {
    font-size: 10px;
  }

  .property-detail--label {
    font-size: 12px;
  }

  .property-detail--value {
    min-height: 0;
    max-height: none;
    height: auto;
    font-size: 14px;
  }

  .property-detail--item {
    border-right: 0;

    &:not(:last-child) {
      border-bottom: 1px solid $neutral-150;
    }
  }

  .slot--container {
    min-height: 0;
    flex: none;
  }

  .slot-section--header {
    h4 {
      font-size: 16px;
    }
  }

  .slot-section--count {
    font-size: 12px;
  }

  .slot-list {
    max-height: 185px;
    flex: none;
    padding-right: 6px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .slot--card {
    min-height: 0;
    padding: 12px;
  }

  .slot-card--eyebrow {
    font-size: 10px;
  }

  .slot-card--label {
    font-size: 14px;
  }

  .slot-date--range {
    grid-template-columns:
      minmax(0, 1fr)
      22px
      minmax(0, 1fr);
  }

  .slot-date--item {
    padding: 10px 8px;
  }

  .slot-date--content {
    span {
      font-size: 10px;
    }

    strong {
      font-size: 12px;
    }
  }

  .change-recommended--button {
    min-height: 42px;
    font-size: 13px;
  }

  .empty-state--wrapper {
    min-height: 420px;
    padding: 24px 0;
  }

  .empty--container {
    padding: 30px 20px;
    border-radius: 20px;

    h3 {
      font-size: 21px;
    }

    p {
      font-size: 14px;
    }
  }

  .empty-state--eyebrow {
    font-size: 11px;
  }

  .empty-state--illustration {
    width: 92px;
    height: 92px;
  }

  .empty-state--shape-one {
    width: 92px;
    height: 92px;
  }

  .empty-state--icon {
    width: 56px;
    height: 56px;
    border-radius: 18px;

    .v-icon {
      font-size: 28px;
    }
  }

  .empty--buy {
    width: 100%;
    font-size: 14px;
  }

  .loading-header {
    min-height: 132px;
    padding: 21px;
    border-radius: 20px;
  }

  .loading-card {
    min-height: 420px;
    border-radius: 20px;
  }
}

/* Small Mobile */
@media (max-width: 390px) {
  .recommended-header--content {
    flex-direction: column;
  }

  .property-detail--grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .property-detail--item-wide {
    grid-column: auto;
  }

  .slot-date--range {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }

  .slot-list {
    max-height: 255px;
  }

  .slot-date--divider {
    display: none;
  }

  .slot-date--item-end {
    text-align: left;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .recommended--list-card,
  .recommended-card--icon,
  .recommended-card--icon .v-icon,
  .slot--card,
  .change-recommended--button {
    transition: none;
  }

  .skeleton::after {
    animation: none;
  }
}
</style>
